import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../config/api';
import notificationService from '../../services/notificationService';

const isNetworkErrorToast = (payload = {}) => {
  const text = `${payload.title || ''} ${payload.message || ''}`.toLowerCase();
  return (
    payload.code === 'ERR_NETWORK'
    || text.includes('network error')
    || text.includes('err_network')
    || text.includes('failed to fetch')
    || text.includes('econnaborted')
    || (typeof navigator !== 'undefined' && navigator.onLine === false && payload.type === 'error' && (
      text.includes('network') || text.includes('offline') || text.includes('timeout')
    ))
  );
};

const isDuplicateToast = (notifications, payload = {}) =>
  notifications.some(
    (n) =>
      n.type === payload.type
      && (n.title || '') === (payload.title || '')
      && (n.message || '') === (payload.message || '')
  );

const stringifyToastMessage = (message) => {
  if (message == null) return '';
  if (typeof message === 'string') return message;
  if (typeof message === 'number' || typeof message === 'boolean') return String(message);
  if (typeof message === 'object') {
    if (typeof message.message === 'string' && message.message.trim()) return message.message;
    if (typeof message.error === 'string' && message.error.trim()) return message.error;
    if (typeof message.data === 'string' && message.data.trim()) return message.data;
  }
  return 'Something went wrong';
};

const reminderIdOf = (item) => String(item?.id || item?.reminderId || '');

const reminderTimeKey = (item) => {
  if (!item) return '';
  const clock = String(item.time || '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (clock) {
    return `${String(Number(clock[1])).padStart(2, '0')}:${clock[2]}`;
  }
  const triggered = String(item.triggeredAt || '').match(/T(\d{2}):(\d{2})/);
  return triggered ? `${triggered[1]}:${triggered[2]}` : '';
};

const asTriggeredReminderItems = (incoming) => {
  if (!incoming) return [];
  if (Array.isArray(incoming.items) && incoming.items.length > 0) {
    return incoming.items.filter((item) => item && (reminderIdOf(item) || item.title));
  }
  if (incoming.id || incoming.reminderId || incoming.title) return [incoming];
  return [];
};

// Async thunk to update dark mode preference on backend
export const updateDarkModePreference = createAsyncThunk(
  'ui/updateDarkModePreference',
  async (darkMode, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('⚠️ No token found, dark mode saved to localStorage only');
        // If not logged in, just return the value (will be saved to localStorage)
        return darkMode;
      }

      console.log('💾 Saving dark mode preference to backend:', darkMode);
      const response = await axios.put(
        `${API_URL}/profile`,
        { darkMode },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const savedDarkMode = response.data.darkMode !== undefined ? response.data.darkMode : darkMode;
      console.log('✅ Dark mode preference saved to backend successfully:', savedDarkMode);
      return savedDarkMode;
    } catch (error) {
      console.error('❌ Failed to update dark mode preference:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Return the value anyway so UI updates even if backend fails
      return darkMode;
    }
  }
);

// Load dark mode from localStorage on initialization (as fallback)
const loadDarkModeFromStorage = () => {
  try {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  } catch (error) {
    console.error('Error loading darkMode from storage:', error);
    return false;
  }
};

// Load sound preferences from localStorage on initialization
const loadSoundPreferencesFromStorage = () => {
  try {
    return notificationService.getAllSoundPreferences();
  } catch (error) {
    console.error('Error loading sound preferences from storage:', error);
    return {};
  }
};

const initialDarkMode = loadDarkModeFromStorage();
const initialSoundPreferences = loadSoundPreferencesFromStorage();

// Apply dark mode to document immediately on initialization
if (initialDarkMode) {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}

const initialState = {
  sidebarOpen: true,
  darkMode: initialDarkMode, // Initialize from localStorage
  notifications: [],
  showNotification: false,
  soundPreferences: initialSoundPreferences, // Initialize from localStorage
  /** Set when a reminder fires via WebSocket; cleared when user dismisses the modal */
  triggeredReminder: null,
  /** Delegated reminder waiting for Accept / Reject / later */
  reminderInvite: null,
  /** Bumped to focus Today's Tasks on pending reminders (e.g. from a multi-reminder popup). */
  pendingRemindersFocusAt: 0,
  /** Active target for mapping a chat message to a task or workflow (Today's Tasks panel). */
  chatMappingTarget: null,
  /** Bumped after a message is mapped so panels can refresh mapped lists. */
  lastChatMapping: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
      // Toggle dark class on document immediately
      if (state.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      // Save to localStorage as fallback
      localStorage.setItem('darkMode', state.darkMode.toString());
    },
    setDarkMode: (state, action) => {
      state.darkMode = action.payload;
      // Apply dark class immediately
      if (action.payload) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      // Save to localStorage as fallback
      localStorage.setItem('darkMode', action.payload.toString());
    },
    addNotification: (state, action) => {
      const payload = { ...(action.payload || {}) };
      payload.message = stringifyToastMessage(payload.message);
      if (isNetworkErrorToast(payload)) {
        return;
      }
      if (isDuplicateToast(state.notifications, payload)) {
        return;
      }
      state.notifications.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ...payload,
      });
      state.showNotification = true;
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.showNotification = false;
    },
    setShowNotification: (state, action) => {
      state.showNotification = action.payload;
    },
    setChannelSoundPreference: (state, action) => {
      const { channelId, enabled } = action.payload;
      state.soundPreferences[channelId] = enabled;
      // Also update localStorage via notificationService
      notificationService.setChannelSoundPreference(channelId, enabled);
    },
    setTriggeredReminder: (state, action) => {
      const incoming = action.payload;
      const incomingItems = asTriggeredReminderItems(incoming);
      if (!incomingItems.length) return;
      const timeKey = reminderTimeKey(incoming) || reminderTimeKey(incomingItems[0]);
      const current = state.triggeredReminder;
      const currentItems = Array.isArray(current?.items)
        ? [...current.items]
        : (current?.id || current?.reminderId ? [current] : []);
      const currentKey = current?.timeKey || reminderTimeKey(currentItems[0]);
      const sameSlot = Boolean(
        currentItems.length
        && timeKey
        && currentKey
        && timeKey === currentKey
      );
      const merged = sameSlot ? currentItems : [];
      incomingItems.forEach((item) => {
        const id = reminderIdOf(item);
        if (id && merged.some((existing) => reminderIdOf(existing) === id)) return;
        merged.push(item);
      });
      state.triggeredReminder = { items: merged, timeKey: timeKey || currentKey || '' };
    },
    clearTriggeredReminder: (state) => {
      state.triggeredReminder = null;
    },
    patchTriggeredReminder: (state, action) => {
      const incoming = action.payload;
      const id = incoming?.id || incoming?._id || incoming?.reminderId;
      if (!id || !state.triggeredReminder) return;
      const items = Array.isArray(state.triggeredReminder.items)
        ? state.triggeredReminder.items
        : (state.triggeredReminder.id || state.triggeredReminder.reminderId ? [state.triggeredReminder] : []);
      const nextItems = items.map((item) => {
        const itemId = item?.id || item?._id || item?.reminderId;
        return itemId === id ? { ...item, ...incoming } : item;
      });
      state.triggeredReminder = { ...state.triggeredReminder, items: nextItems };
    },
    setReminderInvite: (state, action) => {
      const incoming = action.payload;
      if (!incoming || !(incoming.id || incoming.reminderId)) return;
      state.reminderInvite = incoming;
    },
    clearReminderInvite: (state) => {
      state.reminderInvite = null;
    },
    focusPendingReminders: (state) => {
      state.pendingRemindersFocusAt = Date.now();
    },
    setChatMappingTarget: (state, action) => {
      state.chatMappingTarget = action.payload;
    },
    clearChatMappingTarget: (state) => {
      state.chatMappingTarget = null;
    },
    setLastChatMapping: (state, action) => {
      state.lastChatMapping = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateDarkModePreference.fulfilled, (state, action) => {
        // Dark mode preference updated successfully on backend
        // State is already updated by toggleDarkMode/setDarkMode, just log success
        console.log('✅ Dark mode preference saved to backend:', action.payload);
      })
      .addCase(updateDarkModePreference.rejected, (state, action) => {
        console.error('❌ Failed to save dark mode preference:', action.payload);
        // Don't revert the change - keep the local state
      });
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleDarkMode,
  setDarkMode,
  addNotification,
  removeNotification,
  clearNotifications,
  setShowNotification,
  setChannelSoundPreference,
  setTriggeredReminder,
  clearTriggeredReminder,
  patchTriggeredReminder,
  setReminderInvite,
  clearReminderInvite,
  focusPendingReminders,
  setChatMappingTarget,
  clearChatMappingTarget,
  setLastChatMapping,
} = uiSlice.actions;

export default uiSlice.reducer;

